/* eslint-disable no-unused-vars */
import { useEffect, memo, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Buttons, Loader, NotificationModal } from 'components';
import { campaignEnvironment, fieldConvert } from 'constants';
import { manageCampaignsAPI } from 'services/manageCampaigns.service';
import { saveCampaignServiceAPI } from 'services/saveCampaign.service';
import { getDynamicUrl, showPolipop, checkFormBeforeSave } from 'utils';
import { routes } from 'config';
import { useParams } from 'react-router-dom';
import { getCreativeMessage, getCountOfActiveCreatives } from 'store/selectors';
import {
  select_new_on_boarding_icon,
  select_creatives_list,
  select_campaign_error_obj,
  select_current_campaign_obj,
  isAdminSelector,
} from 'store/selectors';
import { cloneDeep } from 'lodash-es';
import {
  generalActions,
  formErrorsCampaign_action,
  targetingBrandsModelsActions,
  targetingLanguagesActions,
} from 'store/actions';

function isNumber(value) {
  return typeof value === 'number' && !isNaN(value) && Number.isFinite(value);
}

const convertExcludedRules = (data) => {
  return {
    typeList: data.typeList.filter(({ selected }) => selected),
    excluded_publishers: data.excluded_publishers
      .filter(({ selected }) => selected)
      .map(({ pb_number }) => pb_number),
    excluded_apps: data.excluded_apps
      .filter(({ selected }) => selected)
      .map(({ pba_number }) => pba_number),
  };
};

const trackingIsCompleted = (trackingError, currentLinks) => {
  const [link1, link2] = currentLinks;
  return trackingError[link1] || trackingError[link2];
};

let timer_id;

const Footer = ({ environment, currentTab }) => {
  const [saveAndStartCampaignAction, saveAndStartResult] =
    saveCampaignServiceAPI.useSaveAndStartCampaignMutation();
  const [saveCampaignAction, result] = saveCampaignServiceAPI.useSaveCampaignDataMutation();
  const [saveExcludedRules, saveExcludedRulesResult] =
    manageCampaignsAPI.useSaveCampaignAdvancedSettingsMutation();
  const [getCheckFactorBid, { data, isFetching, isLoading, isSuccess }] =
    manageCampaignsAPI.useLazyGetFactorBidQuery();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cmp_id, pba_id } = useParams();
  const isSaving = useRef(false);

  const [redirect, setRedirect] = useState(false);

  const isAdmin = useSelector(isAdminSelector);
  const newOnBoardingIcon = useSelector(select_new_on_boarding_icon);
  const creativesList = useSelector(select_creatives_list);
  const countOfActiveCreatives = useSelector(getCountOfActiveCreatives);
  const currentCampaignObj = useSelector(select_current_campaign_obj);
  const { fieldName: creativeMessageFieldName, messageValue } = useSelector(getCreativeMessage);
  const trackingError = useSelector((state) => state?.tracking?.trackingError);
  const mainCampaignObject = useSelector((state) => state?.general?.mainCampaignObject);
  const targeting_languages_tab = useSelector((state) => state?.targetingLanguages?.type);
  const brands_model_type = useSelector((state) => state?.targetingBrandsModels?.type);
  const creativesTracking = useSelector(
    (state) => state?.creatives?.creativeAndTrackingData?.creativesTracking,
  );
  const errorObject = useSelector(select_campaign_error_obj);
  const isOnBoarding = useSelector(
    (state) => state.general.mainCampaignObject?.currentCampaignObj?.isOnBoarding,
  );
  const currentLinksFromDB = useSelector(
    (state) => state?.creatives?.creativeAndTrackingData?.link_state_model_dic,
  );
  const current_cpm_items_array = useSelector(
    (store) => store?.general?.mainCampaignObject?.currentCampaignObj?.campaignBiddings,
  );
  const unsavedData = useSelector(({ creatives, general, targetingCampaign }) => {
    return creatives.unsavedData || general.unsavedData || targetingCampaign.unsavedData;
  });

  const isCpm = currentCampaignObj.bidType === 1;
  const maxBid = isCpm ? window.cpm_bid_maximum : window.bid_maximum;
  const bid = currentCampaignObj.bid;

  // const creativesHavingError = useSelector((state) => state?.formErrorsCampaign.creatives);
  const [showNotification, setShowNotification] = useState(false);
  const [noActiveCreativesMsg, setNoActiveCreativesMsg] = useState(false);

  const currentLinks = useMemo(() => {
    const fields = currentLinksFromDB ? Object.keys(currentLinksFromDB) : [];
    return fields?.map((field) => fieldConvert[field]);
  }, [currentLinksFromDB]);

  const onSaveAndStart = async () => {
    if (isSaving.current) return;
    if (!bid || bid.toString().startsWith('.')) return;

    isSaving.current = true;

    const minBidResult = await getCheckFactorBid(currentCampaignObj);
    if (minBidResult.isError) {
      showPolipop({ error: { data: { title: 'Campaign was not saved' } } });
      isSaving.current = false;
      return;
    }
    if (minBidResult?.data?.minBidAlowed) {
      const minBid = isCpm
        ? window.cpm_bid_minimum
        : isAdmin
        ? window.bid_minimum_admin
        : mainCampaignObject?.disableMinBid
        ? window.bid_minimum_users
        : minBidResult.data.minBidAlowed;
      const { isFormCorrect } = checkFormBeforeSave(
        false,
        creativeMessageFieldName,
        minBid,
        maxBid,
      );
      if (!isFormCorrect) {
        isSaving.current = false;
        return;
      }

      const containActiveCreatives = creativesList?.some(({ status }) => status === 0);
      let doSaveAndStart = true;
      if (!containActiveCreatives) {
        doSaveAndStart = confirm('You have no active creatives. Do you still want to continue?');
      }
      const formDataToSave = createDataForSaving(false);
      if (doSaveAndStart) {
        excludedRules !== null && saveExcludedRulesFunc();
        const res = await saveAndStartCampaignAction(formDataToSave);
        isSaving.current = false;
        if (res?.data) {
          dispatch(formErrorsCampaign_action.resetErrors());
          redirectionAfterSave(res);
          // setShowNotification(false);
          // setRedirect(true);
          return;
        }
      }
      isSaving.current = false;
    }
  };
  const excludedRules = useSelector((state) => state.targetingCampaign.excludeRules);

  const saveExcludedRulesFunc = async () => {
    const saveExcludedRulesRes = await saveExcludedRules({
      data: convertExcludedRules(excludedRules),
      cm_id: cmp_id,
    });
  };

  const createDataForSaving = (isDraft) => {
    const formData = new FormData();
    let newCreativeList = [];
    let newTracking = [];
    if (!isDraft) {
      newCreativeList = structuredClone(creativesList);
      newCreativeList.forEach((item) => {
        if (!isNumber(item?.bn_bannerid)) {
          item.bn_bannerid = null;
        }
      });
      newTracking = structuredClone(creativesTracking);
      newTracking.forEach((item) => {
        if (!isNumber(item?.bn_bannerid)) {
          item.bn_bannerid = null;
        }
      });
    }
    formData.append('bidding', JSON.stringify(current_cpm_items_array));
    formData.append('creatives', JSON.stringify(newCreativeList));
    formData.append('tracking', JSON.stringify(newTracking));
    formData.append('currentCampaignObj', JSON.stringify(currentCampaignObj));
    formData.append('icon', newOnBoardingIcon);
    formData.append('apk', '');

    return formData;
  };

  const saveCampaignDataFunction = async (isDraft) => {
    const formDataToSave = createDataForSaving(isDraft);
    excludedRules !== null && saveExcludedRulesFunc();
    // for (const [key, value] of formDataToSave.entries()) {
    //   console.log(`${key}:`, value ? JSON.parse(value) : value);
    // }

    const res = await saveCampaignAction(formDataToSave);
    return res;
  };

  const redirectionAfterSave = (res) => {
    if (res?.data && environment === campaignEnvironment.new_empty) {
      setShowNotification(false);
      setRedirect(true);
      return;
    }
    if (res?.data && environment === campaignEnvironment.edit) {
      setShowNotification(false);
      setRedirect(true);
      return;
    }
    if (res?.data && environment === campaignEnvironment.duplicate) {
      setShowNotification(false);
      setRedirect(true);
      goToCampaignsList();
      // goToEditCampaign(cmp_id, pba_id);
      return;
    }
  };

  const onSaveAsDraft = async () => {
    if (isSaving.current) return;
    if (!bid || bid.toString().startsWith('.')) return;

    isSaving.current = true;
    setShowNotification(false);
    const res = await saveCampaignDataFunction(true);
    isSaving.current = false;
    if (res?.data) {
      redirectionAfterSave(res);
    }
  };

  const onSaveCampaign = async () => {
    if (isSaving.current) return;
    if (!bid || bid.toString().startsWith('.')) return;

    isSaving.current = true;
    const minBidResult = await getCheckFactorBid(currentCampaignObj);
    if (minBidResult.isError) {
      showPolipop({ error: { data: { title: 'Campaign was not saved' } } });
      isSaving.current = false;
      return;
    }

    if (minBidResult?.data?.minBidAlowed) {
      const minBid = isCpm
        ? window.cpm_bid_minimum
        : isAdmin
        ? window.bid_minimum_admin
        : mainCampaignObject?.disableMinBid
        ? window.bid_minimum_users
        : minBidResult.data.minBidAlowed;
      const { isFormCorrect, isSaveAsDraft, canBeSavedAsDraft } = checkFormBeforeSave(
        true,
        creativeMessageFieldName,
        minBid,
        maxBid,
      );
      if (isSaveAsDraft && canBeSavedAsDraft) {
        setShowNotification(isSaveAsDraft);
        isSaving.current = false;
        return;
      }
      if (!isFormCorrect) {
        isSaving.current = false;
        return;
      }
      if (countOfActiveCreatives === 0) {
        setNoActiveCreativesMsg(true);
        isSaving.current = false;
        return;
      }

      const res = await saveCampaignDataFunction(false);
      isSaving.current = false;
      dispatch(formErrorsCampaign_action.resetErrors());
      redirectionAfterSave(res);
    }
    isSaving.current = false;
  };

  const goToEditCampaign = (cmp_id, pba_id) => {
    let url = getDynamicUrl(routes.list.find((route) => route.id === 'campaign/editCampaign').url, {
      cmp_id,
      pba_id,
    });
    navigate(url);
  };

  const goToCampaignsList = () => {
    let url = routes.list.find((route) => route.id === 'campaign').url;
    // clear targeting section state
    dispatch(targetingBrandsModelsActions.restoreInitialState());
    dispatch(targetingLanguagesActions.restoreInitialState());

    // enable scroll to the current app on Manage Campaigns page
    if (cmp_id) localStorage.setItem('scrollId', cmp_id);
    navigate(url);
  };

  useEffect(() => {
    if (redirect && saveAndStartResult.isSuccess) {
      showPolipop(saveAndStartResult);
      timer_id = setTimeout(() => {
        setRedirect(false);
        goToCampaignsList();
      }, 2500);
    }
    return () => {
      clearTimeout(timer_id);
    };
  }, [saveAndStartResult, redirect]);

  useEffect(() => {
    if (redirect && result.isSuccess) {
      showPolipop(result);
      timer_id = setTimeout(() => {
        setRedirect(false);
        goToCampaignsList();
      }, 2500);
    }
    if (result.isError) {
      showPolipop(result);
    }
    return () => {
      clearTimeout(timer_id);
    };
  }, [result, redirect]);

  // const isDisabledSaveBtn = () => {
  //   const {
  //     app,
  //     campaignName,
  //     target_type,
  //     target_apps_list,
  //     location,
  //     campaignLangs,
  //     brands_audience_to_save,
  //     userAgentsOSType,
  //     userAgentsOSList,
  //     bid,
  //   } = currentCampaignObj;
  //   const { selected_locations } = mainCampaignObject;

  //   //application section
  //   if (!app) return true;
  //   //general information section
  //   if (!campaignName) return true;
  //   //targeting section
  //   //placements tab
  //   if (target_type === 2 && !target_apps_list) return true;
  //   //locations_languages tab
  //   if (+location === 2 && !selected_locations?.length) return true;
  //   if (targeting_languages_tab === 'specific_languages' && !campaignLangs) return true;
  //   //devices_os tab
  //   const { inc } = brands_audience_to_save;
  //   if (brands_model_type === 'specific_brands_models' && !inc) return true;
  //   if (userAgentsOSType === 2 && !userAgentsOSList?.length) return true;
  //   //bidding section
  //   if (!bid) return true;
  //   //conversion tracking section
  //   if (!creativesTracking?.length) return true;
  //   if (creativesTracking?.length && trackingIsCompleted(trackingError, currentLinks)) return true;
  //   //creatives section

  //   //add case for outgoing , if creatives have icon image, atleast one active
  //   return Object.values(errorObject).some((value) => value);
  //   // return false;
  // };

  return (
    <>
      {(result.isLoading || saveAndStartResult.isLoading || isFetching) && (
        <Loader.Global delay={500} />
      )}
      <div
        style={{
          display: 'flex',
          marginTop: '16px',
          marginBottom: '16px',
          gap: '16px',
        }}
      >
        <Buttons.SecondaryNormal
          id='save_start_campaign'
          onClick={onSaveAndStart}
          isDisabled={result.isLoading || result.isSuccess}
          // disabled={isDisabledSaveBtn() || !unsavedData}
        >
          Save & Start
        </Buttons.SecondaryNormal>
        <Buttons.SecondaryNormal
          id='save_campaign'
          onClick={onSaveCampaign}
          isDisabled={result.isLoading || result.isSuccess}
          // disabled={isDisabledSaveBtn() || !unsavedData}
        >
          Save Campaign
        </Buttons.SecondaryNormal>
        <Buttons.SecondaryNormal id='cancel_campaign' onClick={goToCampaignsList}>
          Cancel
        </Buttons.SecondaryNormal>
      </div>
      {showNotification && (
        <NotificationModal
          title='Unable to save'
          id='impossible_to_save'
          iconType={NotificationModal.iconTypes.warning}
          iconSign='i'
          cancelBtnType={NotificationModal.btnTypes.SecondaryNormal}
          cancelBtnText='Save as Draft'
          onCancel={onSaveAsDraft}
          onConfirm={() => {
            setShowNotification(false);
          }}
          confirmBtnType={NotificationModal.btnTypes.PrimaryNormal}
          confirmBtnText='Ok'
        >
          Please complete the &apos;Creatives&apos; section before proceeding. <br />
          Note: Uploaded images and tracking links will not be saved in draft mode.
        </NotificationModal>
      )}
      {noActiveCreativesMsg && (
        <NotificationModal
          title=''
          id='no_active_creatives'
          iconType={NotificationModal.iconTypes.info}
          iconSign='i'
          onConfirm={() => {
            setNoActiveCreativesMsg(false);
          }}
          confirmBtnType={NotificationModal.btnTypes.PrimaryNormal}
          confirmBtnText='Ok'
        >
          At least one creative must be active to save the campaign.
        </NotificationModal>
      )}
    </>
  );
};

export default memo(Footer);
